// @flow
import {providerUrlV2} from './config';
import {VideoMeta} from './videoMeta';
import {Logger} from './logger';
import {Http} from './http';
import _defaultJSON from './defaultJSON';

/**
 * Proveedor de la API v2 (`/json/...`).
 *
 * Sirve el mismo VideoMeta que jsonProvider, pero a partir de los JSON planos de la API nueva:
 * sin hashes md5, sin el XML de settings embebido y con el origen del stream ya resuelto.
 */
class JSONProvider2 {
  isLive: boolean = false;
  isVod: boolean = false;
  embed: string;
  id: string;
  user: String;

  providerUrl: string;
  logger: Logger;
  config: any;
  defaultJSON: any;

  constructor(config: any) {
    this.config = config;
    this.logger = new Logger('JSONProvider2');
    this.providerUrl = this.config.providerUrlV2 !== undefined && this.config.providerUrlV2 !== null ? this.config.providerUrlV2 : providerUrlV2;
    this.embed = this.config.embed;
    this.id = this.config.id;
    // En la v2 el vod se identifica por el guid del vídeo y el live por el playerKey de la url.
    this.isVod = this.id !== undefined && this.id !== null && !!this.id;
    this.isLive = !this.isVod;
  }

  async init() {
    try {
      if (this.isVod) return await this.decodeVOD();
      if (this.isLive) return await this.decodeLIVE();
      throw new Error('404 not found');
    } catch (e) {
      this.logger.debug(e);
      return e;
    }
  }

  async decodeLIVE() {
    const playerData = await Http.execute(`${this.providerUrl}/json/player/${this.embed}`, null, 'GET');
    let result: VideoMeta = this._decodePlayer(playerData);
    result.isLive = true;
    result.isActive = true;
    result.user = playerData.user;
    this.user = result.user;
    result.streamNameLive = playerData.streamname;

    await this._loadDefaultJSON(result.streamNameLive, playerData.src, 'urls');

    result.isDvr = playerData.isDvr === true;
    result.urls = {hls: playerData.src.replace('.f4m', '.m3u8').replace('http:', 'https:')};
    if (result.isDvr) result.urls.hls = `${result.urls.hls}?DVR`;

    return result;
  }

  async decodeVOD() {
    const videoData = await Http.execute(`${this.providerUrl}/json/video/${this.id}`, null, 'GET');
    const playerData = await Http.execute(`${this.providerUrl}/json/player/${videoData.playerKey}`, null, 'GET');
    let result: VideoMeta = this._decodePlayer(playerData);
    result.isLive = false;
    result.isDvr = false;
    result.user = videoData.user !== undefined && videoData.user ? videoData.user : playerData.user;
    this.user = result.user;

    await this._loadDefaultJSON(null, videoData.src, 'vod');

    result.basics = {
      title: videoData.title,
      description: videoData.description,
      tags: null
    };
    result.isActive = videoData.videoIsActive == '1' || videoData.videoIsActive == 'true' || videoData.videoIsActive === undefined;
    result.duration = videoData.duration !== undefined && videoData.duration ? videoData.duration : 1;
    try {
      result.publishDate = videoData.vdoFechaCreacion ? new Date(videoData.vdoFechaCreacion) : null;
    } catch (e) {
      this.logger.debug(e);
    }
    // El poster del player manda sobre la miniatura del vídeo.
    if (!result.images.principal) result.images.principal = this._mediaUrl(videoData.prevPic);

    const storage = this.defaultJSON.storage[0].replace('http:', 'https:');
    result.urls = {
      hls: videoData.src.replace('http:', 'https:'),
      mp4: (videoData.streams || []).map(stream => `${storage}/video/${result.user}/${stream.name}`)
    };

    return result;
  }

  /**
   * Campos comunes a live y vod del JSON de player v2.
   * @param {any} playerData respuesta de /json/player/{playerKey}
   * @returns {VideoMeta} videoMeta parcial
   */
  _decodePlayer(playerData: any): VideoMeta {
    let result: VideoMeta = {};
    result.playerId = playerData.playerKey;
    // El chat ya no se identifica con el ss de la v1.
    result.playerSS = 0;
    result.basics = {
      title: playerData.title,
      description: null,
      tags: null
    };
    result.autoPlay = playerData.autoPlay === true;
    result.isAudio = playerData.onlyAudio === true;
    result.showInfoBtn = playerData.menuEnabled !== false;
    result.FGNToken = playerData.tokenAuthEnabled === true;
    result.chatEnabled = playerData.chatEnable === true;
    result.chatUrl = playerData.chatUrl;

    result.customization = {
      checkAdBlock: playerData.checkAdBlock === true,
      color: this._color(playerData.themeColor),
      showTitle: playerData.showTitle === true,
      hideBitrates: playerData.hideBitrates === true
    };

    result.watermark = {
      pos: this._logoPosition(playerData.logoPosition),
      src: playerData.logoImageUrl,
      target: playerData.logoLinkUrl,
      isVisible: playerData.logoVisible === true
    };
    result.branding = {src: playerData.brandImageUrl, url: playerData.brandLinkUrl};
    result.images = {
      principal: this._mediaUrl(playerData.posterImage),
      domainBlocked: playerData.domainBlockedImage || false,
      geoBlocked: playerData.geoBlockingImage || false
    };

    result.share = {
      facebook: playerData.socialFacebook === true,
      twitter: playerData.socialTwitter === true,
      source: playerData.socialShareVideo === true,
      download: playerData.socialDownload === true
    };

    // No hay interruptor de encendido: la lista manda. Si viene vacía no hay nada que restringir,
    // que es justo como el player interpreta una lista vacía.
    result.allowedDomains = this._split(playerData.domainAllowed);
    // En la v2 los países listados son los permitidos, al revés que en la v1.
    result.geoBlocking = this._split(playerData.geoBlockingAllowed).map(c => c.split(':')[0]);
    result.geoBlockingMode = 'allow';

    result.analytics = this._analytics(playerData.playerAnalytics);
    result.ads = {
      ivc: {
        pre_roll: {skipOffset: 0, src: null},
        mid_roll: {skipOffset: 0, src: null},
        pos_roll: {skipOffset: 0, src: null}
      },
      vast: {url: playerData.adsEnabled === true && playerData.vmap ? playerData.vmap : null}
    };
    return result;
  }

  /**
   * Descarga la lista de CDNs y la ordena para que el host que ya trae el src sea el primero
   * que se reintenta, que es el que la API indica que está sirviendo el contenido.
   * @param {string} stream nombre del stream (sólo live)
   * @param {string} src url del contenido
   * @param {string} key lista a ordenar: `urls` en live y `vod` en vod
   * @returns {Promise<void>} void
   */
  async _loadDefaultJSON(stream: ?string, src: string, key: string): Promise<void> {
    // Copia propia: el defaultJSON de configInput puede venir compartido entre players.
    this.defaultJSON = Object.assign(
      {},
      this.config.defaultJSON !== undefined && this.config.defaultJSON ? this.config.defaultJSON : await _defaultJSON(this.user, this.config, stream, 2)
    );
    /**
     * Funcionalidad que permite poner en el json del javascript al instanciar el player rutas de cdn por usuario
     */
    if (this.defaultJSON[this.user] !== undefined) {
      try {
        this.defaultJSON.urls = this.defaultJSON[this.user].urls;
      } catch (e) {
        this.logger.debug(e);
      }
    }
    this.defaultJSON[key] = this._sortByHost(this.defaultJSON[key], src);
  }

  /**
   * @param {string[]} urls lista de CDNs
   * @param {string} src url del contenido
   * @returns {string[]} lista empezando por el host del src
   */
  _sortByHost(urls: string[], src: string): string[] {
    try {
      const host = /^(https?:)?\/\/[^/]+/.exec(src)[0].replace('http:', 'https:');
      const rest = (urls || []).filter(url => url.replace('http:', 'https:').replace(/\/$/, '') !== host);
      return [host].concat(rest);
    } catch (e) {
      this.logger.debug(e);
      return urls;
    }
  }

  /**
   * @param {any} playerAnalytics lista de analíticas del player v2
   * @returns {any} bloque analytics del videoMeta
   */
  _analytics(playerAnalytics: any): any {
    let analytics = {google: {key: null}, realtime: {disabled: false}, ivc: {disabled: false}};
    if (!Array.isArray(playerAnalytics)) return analytics;
    for (let item of playerAnalytics) {
      const provider = item.provider !== undefined && item.provider ? String(item.provider).toLowerCase() : '';
      switch (provider) {
        case 'google':
          analytics.google = {key: item.accountId};
          break;
        case 'kantar':
          analytics['kantarmedia'] = {kantarID: item.accountId, kantarET: item.accountExtra};
          break;
        case 'comscore':
          analytics['comscore'] = {id: item.accountId};
          break;
        case 'youbora':
          analytics['youbora'] = {id: item.accountId, title: item.accountExtra};
          break;
        case 'adobe':
          analytics['adobe'] = {id: item.accountId};
          break;
        default:
          this.logger.debug(`unknown analytics provider: ${provider}`);
      }
    }
    return analytics;
  }

  /**
   * Descarta los marcadores vacíos de la API (`string`, `false`) y se queda con una url de imagen.
   * @param {string} value url candidata
   * @returns {?string} url o null
   */
  _mediaUrl(value: ?string): ?string {
    if (value === undefined || value === null) return null;
    const text = String(value).trim();
    if (!text || text.toLowerCase() === 'string' || text.toLowerCase() === 'false') return null;
    if (/^(https?:)?\/\//i.test(text) || text.charAt(0) === '/' || /\.(jpe?g|png|gif|webp|svg)(\?.*)?$/i.test(text)) return text;
    return null;
  }

  /**
   * @param {string} value lista separada por comas
   * @returns {string[]} elementos no vacíos
   */
  _split(value: ?string): string[] {
    if (value === undefined || value === null || !String(value).length) return [];
    return (
      String(value)
        .split(',')
        .map(item => item.trim())
        // La v1 usaba el literal "false" como marcador de lista vacía y puede llegar por datos migrados
        .filter(item => item.length && item.toLowerCase() !== 'false')
    );
  }

  /**
   * La v2 manda el color en formato css y customize.js espera el `0xRRGGBB` de la v1 con la
   * opacidad aparte. Si el color trae canal alfa (`#RRGGBBAA`, `#RGBA` o `rgba()`) se respeta;
   * si no, se deja la opacidad a 0 para que customize aplique la suya por defecto.
   * @param {string} themeColor color del player
   * @returns {{value: ?string, alpha: number}} color y opacidad
   */
  _color(themeColor: ?string): any {
    let color = {value: null, alpha: 0};
    if (themeColor === undefined || themeColor === null || !String(themeColor).trim().length) return color;
    const value = String(themeColor).trim();

    const rgba = /^rgba?\(\s*(\d+)\s*[,\s]\s*(\d+)\s*[,\s]\s*(\d+)\s*(?:[,/]\s*([\d.]+%?)\s*)?\)$/i.exec(value);
    if (rgba) {
      color.value = `0x${this._hex(rgba[1])}${this._hex(rgba[2])}${this._hex(rgba[3])}`;
      color.alpha = this._alpha(rgba[4]);
      return color;
    }

    let hex = value.replace('#', '').replace(/^0x/i, '');
    if (!/^[0-9a-f]+$/i.test(hex)) return color;
    // Las formas cortas `#RGB` y `#RGBA` duplican cada dígito
    if (hex.length === 3 || hex.length === 4) {
      hex = hex
        .split('')
        .map(c => `${c}${c}`)
        .join('');
    }
    if (hex.length === 8) {
      color.alpha = Math.round((parseInt(hex.slice(6, 8), 16) / 255) * 100) / 100;
      hex = hex.slice(0, 6);
    }
    if (hex.length !== 6) return color;
    color.value = `0x${hex}`;
    return color;
  }

  /**
   * @param {string} value componente de color 0-255
   * @returns {string} componente en dos dígitos hexadecimales
   */
  _hex(value: string): string {
    const component = Math.max(0, Math.min(255, parseInt(value, 10)));
    return component < 16 ? `0${component.toString(16)}` : component.toString(16);
  }

  /**
   * @param {string} value opacidad en 0-1 o en porcentaje
   * @returns {number} opacidad 0-1, donde 0 significa "usa la de por defecto"
   */
  _alpha(value: ?string): number {
    if (value === undefined || value === null || !value.length) return 0;
    const alpha = value.indexOf('%') > -1 ? parseFloat(value) / 100 : parseFloat(value);
    if (isNaN(alpha) || alpha < 0) return 0;
    return alpha > 1 ? 1 : alpha;
  }

  /**
   * @param {string} logoPosition posición 0-8 del logo
   * @returns {string} posición del watermark
   */
  _logoPosition(logoPosition: ?string): string {
    switch (parseInt(logoPosition) + 1) {
      case 3:
        return 'top-right';
      case 7:
      case 8:
        return 'bottom-left';
      case 9:
        return 'bottom-right';
      case 1:
      case 2:
      case 4:
      case 5:
      case 6:
        return 'top-left';
      default:
        return '';
    }
  }
}

export {JSONProvider2};
